import { Box, Button, HStack, Icon, Text } from '@chakra-ui/react';
import {
  FileCopies,
  Minus,
  Plus,
  TrackArticle,
  TrackAssessment,
  TrackFile,
  TrackImportedContent,
  TrackScorm,
  TrackVideo,
} from '@/client/components/icons/ContinuIcons';

import type { AssignmentFormData } from '../../types/assignmentTypes';
import AssignmentFormPreviewHeader from './AssignmentFormPreviewHeader';
import { ContentTypes } from '@/client/services/api/graphql/gql/graphql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ContentFormPreviewProps {
  settings: AssignmentFormData['settings'];
  isLocked?: boolean;
  renderStatus?: boolean;
  isModalPreview?: boolean;
}

export default function ContentFormPreview({
  settings,
  isLocked = false,
  renderStatus = false,
  isModalPreview = false,
}: ContentFormPreviewProps) {
  const { t } = useTranslation();

  const [showAll, setShowAll] = useState(false);

  const { responses } = settings;

  const firstSevenResponses = responses.slice(0, 7);
  const remainingResponses = responses.slice(7);

  const objectTypeIconMap: Record<string, React.ElementType> = {
    [ContentTypes.Article]: TrackArticle,
    article: TrackArticle,
    [ContentTypes.Assessment]: TrackAssessment,
    assessment: TrackAssessment,
    [ContentTypes.File]: TrackFile,
    file: TrackFile,
    [ContentTypes.Scorm]: TrackScorm,
    scorm: TrackScorm,
    [ContentTypes.Video]: TrackVideo,
    video: TrackVideo,
    [ContentTypes.Track]: TrackArticle,
    track: TrackArticle,
    [ContentTypes.Journey]: TrackArticle,
    journey: TrackArticle,
    [ContentTypes.ImportedContent]: TrackImportedContent,
    imported_content: TrackImportedContent,
    [ContentTypes.Event]: TrackArticle,
    event: TrackArticle,
    [ContentTypes.Poll]: TrackArticle,
    poll: TrackArticle,
    [ContentTypes.Presentation]: TrackArticle,
    presentation: TrackArticle,
    [ContentTypes.Quiz]: TrackArticle,
    quiz: TrackArticle,
    [ContentTypes.Media]: TrackArticle,
    media: TrackArticle,
    [ContentTypes.Workshop]: TrackArticle,
    workshop: TrackArticle,
    [ContentTypes.MultiSessionWorkshop]: TrackArticle,
    multi_session_workshop: TrackArticle,
  };

  return (
    <Box>
      <AssignmentFormPreviewHeader
        formName="content"
        icon={FileCopies}
        title={
          isLocked
            ? t('admin.assignments.step.content.locked')
            : t('admin.assignments.step.content')
        }
        renderStatus={renderStatus}
        isLocked={isLocked}
      />

      {responses.length > 0 && (
        <Box
          paddingY={isModalPreview ? 0 : 6}
          paddingX={isModalPreview ? 0 : 6}
          borderTop="1px solid"
          borderTopColor={isModalPreview ? 'transparent' : 'neutral.100'}
        >
          {firstSevenResponses.map((response) => {
            if (!response.content) return null;

            return (
              <HStack
                key={response.content?.id}
                gap={2}
                paddingY={3}
                borderBottomColor="neutral.100"
              >
                <Icon as={objectTypeIconMap[response.content.contentType]} boxSize={6} />

                <Text fontSize="14px" fontWeight={500}>
                  {response.content?.title}
                </Text>
              </HStack>
            );
          })}

          {showAll && (
            <Box>
              {remainingResponses.map((response) => {
                if (!response.content) return null;

                return (
                  <HStack
                    key={response.content?.id}
                    gap={2}
                    paddingY={3}
                    borderBottomColor="neutral.100"
                  >
                    <Icon as={objectTypeIconMap[response.content.contentType]} boxSize={6} />

                    <Text fontSize="14px" fontWeight={500}>
                      {response.content?.title}
                    </Text>
                  </HStack>
                );
              })}
            </Box>
          )}

          {responses.length > 7 && (
            <Button
              backgroundColor="neutral.0"
              border="1px solid"
              borderColor="neutral.100"
              width="full"
              marginBottom={6}
              leftIcon={<Icon as={showAll ? Minus : Plus} boxSize={3} />}
              color="neutral.800"
              onClick={() => setShowAll(!showAll)}
              _hover={{
                backgroundColor: 'neutral.50',
              }}
            >
              {`${t('admin.assignments.content.show')} ${
                showAll
                  ? t('admin.assignments.content.less')
                  : `${remainingResponses.length} ${t('admin.assignments.content.more')}`
              }`}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
